import React, { memo, useCallback, useState, useMemo, useEffect } from 'react'
import tw from 'twin.macro'
import { useDispatch } from 'react-redux'
import { Modal } from '@mui/material'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { BasicRadioGroup, BasicRadioOption } from 'src/components/shared/form/BasicRadioGroup'
import { Formik } from 'formik'
import { FormikDatePicker } from 'src/components/shared/form/formik/FormikDatePicker'
import { Typography, Divider } from '@mui/material'
import { colors } from 'src/theme'
import { Button } from 'src/components/shared/Button'
import {
  FetchContractorDeactivationDates,
  SetContractorDeactivationDate,
} from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { DeactivationDateOption } from 'src/sagas/contractor'
import { handleApiError } from 'src/utils/errorHandlers'
import { showToast } from 'src/utils/toast'
import dayjs from 'dayjs'
import { collectedDataStepsTitle, CollectedDataStepType } from 'src/constants/collectedData'

interface Props {
  isOpen: boolean
  contractorId: string
  deactivationScheduledAt?: string | null
  onClose(): void
  onRefresh(): Promise<void> | void
}

const MANUAL_DATE_OPTION_VALUE = 'manual'

interface FormValues {
  selectedDate: string
  manualDate: string | null
}

const SetDeactivationDateModalComponent = ({
  isOpen,
  contractorId,
  deactivationScheduledAt,
  onClose,
  onRefresh,
}: Props) => {
  const dispatch = useDispatch()

  const [deactivationDateOptions, setDeactivationDateOptions] = useState<DeactivationDateOption[]>(
    [],
  )
  const [isLoadingOptions, setIsLoadingOptions] = useState(false)
  const container = useMemo(() => document.getElementById('root'), [])

  useEffect(() => {
    if (isOpen) {
      const fetchDeactivationDates = async () => {
        setIsLoadingOptions(true)
        try {
          const options: DeactivationDateOption[] = await createAsyncAction(
            dispatch,
            FetchContractorDeactivationDates.request({
              partnerDriverId: contractorId,
            }),
          )
          setDeactivationDateOptions(Array.isArray(options) ? options : [])
        } catch (err) {
          setDeactivationDateOptions([])
        } finally {
          setIsLoadingOptions(false)
        }
      }

      fetchDeactivationDates()
    }
  }, [isOpen, contractorId, dispatch])

  const expirationDateOptions = useMemo<BasicRadioOption[]>(() => {
    const options: BasicRadioOption[] = []

    deactivationDateOptions.forEach((option) => {
      const formattedDate = dayjs(option.date).format('MMM D, YYYY')
      const sourceLabel =
        collectedDataStepsTitle[option.source as CollectedDataStepType] ||
        option.source
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      options.push({
        label: `${formattedDate} - ${sourceLabel}`,
        value: option.date,
      })
    })

    options.push({
      label: 'Enter date manually',
      value: MANUAL_DATE_OPTION_VALUE,
    })

    return options
  }, [deactivationDateOptions])

  const initialSelectedValue = useMemo(() => {
    if (deactivationScheduledAt) {
      const matchingOption = expirationDateOptions.find(
        (option) => option.value === deactivationScheduledAt,
      )

      if (matchingOption) {
        return String(matchingOption.value)
      }

      return MANUAL_DATE_OPTION_VALUE
    }

    return ''
  }, [deactivationScheduledAt, expirationDateOptions])

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      const rawDate =
        values.selectedDate === MANUAL_DATE_OPTION_VALUE ? values.manualDate : values.selectedDate

      if (!rawDate) {
        return
      }

      const dateToUse = dayjs(rawDate).format('YYYY-MM-DD')

      try {
        await createAsyncAction(
          dispatch,
          SetContractorDeactivationDate.request({
            partnerDriverId: contractorId,
            deactivationScheduledAt: dateToUse,
          }),
        )
        showToast('Deactivation date has been set!', { variant: 'success' })
        await onRefresh()
        onClose()
      } catch (err) {
        handleApiError(err)
      }
    },
    [contractorId, dispatch, onRefresh, onClose],
  )

  return (
    <Modal
      container={container}
      open={isOpen}
      onClose={onClose}
      aria-labelledby="set-deactivation-date-title"
      css={tw`flex justify-center items-center backdrop-blur`}
    >
      <div
        data-testid="contractor-details-set-deactivation-date-modal"
        css={tw`bg-white rounded-lg w-[400px]`}
      >
        <ModalHeader
          title="Set deactivation date"
          onClose={onClose}
          closeButtonDataTestId="contractor-details-set-deactivation-date-modal-close-btn"
        />
        <Formik<FormValues>
          key={expirationDateOptions.length}
          initialValues={{
            selectedDate: initialSelectedValue,
            manualDate:
              initialSelectedValue === MANUAL_DATE_OPTION_VALUE && deactivationScheduledAt
                ? deactivationScheduledAt
                : null,
          }}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue, handleSubmit: formikHandleSubmit }) => (
            <form onSubmit={formikHandleSubmit}>
              <div css={tw`p-4`}>
                <Typography
                  css={tw`mb-4 text-[14px]`}
                  variant="body2"
                  color={colors.GRAY_COOL_GRAY}
                >
                  Select when this contractor should be deactivated.
                </Typography>
                <Divider css={tw`mb-4`} />
                {isLoadingOptions ? (
                  <Typography variant="body2" color={colors.GRAY_COOL_GRAY} css={tw`py-4`}>
                    Loading options...
                  </Typography>
                ) : (
                  <BasicRadioGroup
                    name="selectedDate"
                    label="Select deactivation date"
                    value={values.selectedDate}
                    options={expirationDateOptions}
                    onChange={(e) => {
                      setFieldValue('selectedDate', e.target.value)

                      if (e.target.value !== MANUAL_DATE_OPTION_VALUE) {
                        setFieldValue('manualDate', null)
                      }
                    }}
                  />
                )}
                {values.selectedDate === MANUAL_DATE_OPTION_VALUE && (
                  <div css={tw`mt-4`}>
                    <FormikDatePicker
                      name="manualDate"
                      label="Enter date manually"
                      minDate={dayjs().add(1, 'day')}
                      minDateError="Deactivation date must be in the future"
                    />
                  </div>
                )}
              </div>
              <div
                css={tw`px-4 py-3 flex justify-between border-0 border-t border-solid border-[#EDEDED]`}
              >
                <Button
                  variant="contained"
                  color="grey"
                  size="small"
                  onClick={onClose}
                  className={tw`w-[100px]`}
                  data-testid="contractor-details-set-deactivation-date-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  type="submit"
                  className={tw`w-[100px]`}
                  disabled={
                    !values.selectedDate ||
                    (values.selectedDate === MANUAL_DATE_OPTION_VALUE && !values.manualDate)
                  }
                  data-testid="contractor-details-set-deactivation-date-save-btn"
                >
                  Save
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

export const SetDeactivationDateModal = memo(SetDeactivationDateModalComponent)
